"use client";

import {
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  ExternalLink,
  FileText,
  Loader2,
  ShieldCheck,
  ShieldOff,
} from "lucide-react";

import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { Card, CardContent } from "@/app/_module/components/ui/card";

import { CommissionBadge } from "@/app/(authenticated)/quote-enroll/_modules/components/CommissionBadge";
import { CarrierLogo } from "@/app/_module/components/CarrierLogo";

import { useTechficientAgentDetails } from "./useTechficientDashboard";

interface TechficientAgentPanelProps {
  userId: string;
}

const fmtCurrency = (n: number | null | undefined) => {
  if (n == null) return "—";
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  const abs = Math.round(Math.abs(x));
  return `${x < 0 ? "-" : ""}$${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const fmtDate = (s: string | null | undefined) =>
  s
    ? new Date(s).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

/**
 * Per-agent Techficient drill-down. Mounts on AgentDetailsView, only fetches
 * when the section is expanded — keeps the existing agent overview lightweight.
 *
 * Shows everything that's load-bearing for the Commissions story:
 *   - Provisioning state (synced? eligible? last synced when?)
 *   - State licenses (so the floor can see who's licensed in which states)
 *   - Carrier contracts (writing numbers per carrier)
 *   - Recent cases joined with policy + commission (status + commission badge)
 *   - Commission balance (advanced / earned / charged back / net pending)
 */
export function TechficientAgentPanel({ userId }: TechficientAgentPanelProps) {
  // Always expanded — the collapse toggle was removed on purpose; the panel
  // sits directly under the profile hero and should always show its content.
  const { data, isLoading, error } = useTechficientAgentDetails(userId, true);

  return (
    <Card>
      <CardContent className="p-0">
        <div className="w-full flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold text-gray-900">
              Quote &amp; Enroll
            </h3>
            {data && (
              <Badge
                variant="secondary"
                className={
                  data.provisioning.provisioned
                    ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-50 ml-1"
                    : "bg-amber-50 text-amber-700 hover:bg-amber-50 ml-1"
                }
              >
                {data.provisioning.provisioned ? "Registered" : "Not registered"}
              </Badge>
            )}
          </div>
          {data?.provisioning.lastSyncedAt && (
            <span className="text-[11px] text-gray-400">
              Synced {fmtDate(data.provisioning.lastSyncedAt)}
            </span>
          )}
        </div>

          <div className="p-4 space-y-5">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 text-primary animate-spin" />
              </div>
            )}

            {error && (
              <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-md px-3 py-3">
                <AlertTriangle className="h-4 w-4 text-gray-400" />
                This agent isn&apos;t enrolled yet — no quotes, cases, or
                commissions on file.
              </div>
            )}

            {data && (
              <>
                <ProvisioningSection data={data} />
                <CommissionBalanceSection data={data} />
                <CasesSection data={data} />
                <QuotesSection data={data} />
              </>
            )}
          </div>
      </CardContent>
    </Card>
  );
}

// ───────────────────────── Provisioning ─────────────────────────

function ProvisioningSection({
  data,
}: {
  data: NonNullable<
    ReturnType<typeof useTechficientAgentDetails>["data"]
  >;
}) {
  const { provisioning } = data;

  const stateLicenses = provisioning.stateLicenses || [];
  const carrierContracts = provisioning.carrierContracts || [];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <StatTile
        icon={provisioning.provisioned ? ShieldCheck : ShieldOff}
        tone={provisioning.provisioned ? "emerald" : "amber"}
        label="Enrollment"
        value={provisioning.provisioned ? "Registered" : "Not registered"}
        hint={
          provisioning.lastSyncedAt
            ? `Synced ${fmtDate(provisioning.lastSyncedAt)}`
            : "Not registered yet"
        }
      />
      <StatTile
        icon={ShieldCheck}
        tone="blue"
        label="States Licensed"
        value={String(stateLicenses.length)}
        hint={
          stateLicenses.length
            ? stateLicenses.map((l) => l.stateCode).join(" · ")
            : "None"
        }
      />
      <StatTile
        icon={ShieldCheck}
        tone="blue"
        label="Carriers Appointed"
        value={String(carrierContracts.length)}
        hint={
          carrierContracts.length
            ? `${carrierContracts.length} active appointment${
                carrierContracts.length === 1 ? "" : "s"
              }`
            : "None"
        }
      />
    </div>
  );
}

function StatTile({
  icon: Icon,
  tone,
  label,
  value,
  hint,
}: {
  icon: React.ComponentType<{ className?: string }>;
  tone: "emerald" | "amber" | "blue";
  label: string;
  value: string;
  hint: string;
}) {
  const palette =
    tone === "emerald"
      ? "bg-emerald-50 text-emerald-600"
      : tone === "amber"
      ? "bg-amber-50 text-amber-600"
      : "bg-blue-50 text-blue-600";
  return (
    <div className="rounded-lg border bg-white p-3 flex items-start gap-3">
      <span
        className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${palette}`}
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">
          {label}
        </p>
        <p className="text-lg font-semibold text-gray-900 tabular-nums leading-tight">
          {value}
        </p>
        <p
          className="text-[11px] text-gray-400 truncate"
          title={hint}
        >
          {hint}
        </p>
      </div>
    </div>
  );
}

// ───────────────────────── Commission balance ─────────────────────────

function CommissionBalanceSection({
  data,
}: {
  data: NonNullable<
    ReturnType<typeof useTechficientAgentDetails>["data"]
  >;
}) {
  const b = data.commissionBalance;
  const net =
    Number(b.advanced || 0) + Number(b.earned || 0) - Number(b.chargedBack || 0);

  const cells = [
    { label: "Advanced", value: b.advanced, tone: "text-blue-700" },
    { label: "Earned", value: b.earned, tone: "text-emerald-700" },
    {
      label: "Charged Back",
      value: b.chargedBack,
      tone: "text-rose-600",
      prefix: b.chargedBack > 0 ? "−" : "",
    },
    {
      label: "Net Pending",
      value: b.netPending,
      tone: "text-amber-700",
    },
    { label: "Total Net", value: net, tone: "text-gray-900 font-semibold" },
  ];

  return (
    <div>
      <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wide mb-2">
        Commission balance
      </h4>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {cells.map((c) => (
          <div key={c.label} className="rounded-md bg-gray-50 px-3 py-2">
            <p className="text-[10px] uppercase tracking-wide text-gray-500 font-medium">
              {c.label}
            </p>
            <p className={`text-base tabular-nums mt-0.5 ${c.tone}`}>
              {c.prefix || ""}
              {fmtCurrency(c.value)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ───────────────────────── Cases ─────────────────────────

function CasesSection({
  data,
}: {
  data: NonNullable<
    ReturnType<typeof useTechficientAgentDetails>["data"]
  >;
}) {
  const cases = data.cases || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">
          Recent cases ({cases.length})
        </h4>
      </div>
      {cases.length === 0 ? (
        <p className="text-xs text-gray-400 px-3 py-6 text-center bg-gray-50 rounded-md">
          No Quote &amp; Enroll cases for this agent yet.
        </p>
      ) : (
        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Carrier · Product</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Face</th>
                <th className="text-right px-3 py-2">Premium</th>
                <th className="text-left px-3 py-2">Commission</th>
                <th className="text-right px-3 py-2"> </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {cases.slice(0, 12).map((c) => (
                <tr key={c.id}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <CarrierLogo name={c.carrierName} size={24} />
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate max-w-[200px]">
                          {c.carrierName || c.carrierCode || "—"}
                        </div>
                        <div className="text-[10px] text-gray-500 truncate max-w-[200px]">
                          {c.productName || c.productCode || ""}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill status={c.caseStatus} />
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtCurrency(c.approvedFaceAmount || c.appliedFaceAmount)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {fmtCurrency(
                      c.approvedModalPremium || c.appliedModalPremium,
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <CommissionBadge commission={c.commission} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {c.launchUrl && (
                      <a
                        href={c.launchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 text-[11px]"
                      >
                        eApp
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status?: string }) {
  if (!status) return <span className="text-gray-400 text-[11px]">—</span>;
  const s = status.toLowerCase();
  let tone = "bg-gray-100 text-gray-700";
  let Icon = CheckCircle2;
  if (
    s.includes("sold") ||
    s.includes("approved") ||
    s.includes("issued") ||
    s.includes("force")
  ) {
    tone = "bg-emerald-50 text-emerald-700";
  } else if (
    s.includes("lapse") ||
    s.includes("declin") ||
    s.includes("cancel") ||
    s.includes("nto") ||
    s.includes("withdrawn")
  ) {
    tone = "bg-rose-50 text-rose-700";
  } else if (
    s.includes("pending") ||
    s.includes("review") ||
    s.includes("open") ||
    s.includes("submitted") ||
    s.includes("underwriting") ||
    s.includes("initiated")
  ) {
    tone = "bg-amber-50 text-amber-700";
  }
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${tone}`}
    >
      {status}
    </span>
  );
}

// ───────────────────────── Quotes ─────────────────────────

function QuotesSection({
  data,
}: {
  data: NonNullable<
    ReturnType<typeof useTechficientAgentDetails>["data"]
  >;
}) {
  const quotes = data.quotes || [];

  if (quotes.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <FileText className="h-3.5 w-3.5 text-gray-400" />
        <h4 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">
          Recent quotes ({quotes.length})
        </h4>
      </div>
      <div className="border rounded-md max-h-48 overflow-y-auto divide-y">
        {quotes.slice(0, 10).map((q) => (
          <div
            key={q.id}
            className="flex items-center justify-between text-xs px-3 py-2"
          >
            <span className="text-gray-700">{fmtDate(q.createdAt)}</span>
            <Badge variant="secondary" className="text-[10px]">
              {q.resultCount} carriers
            </Badge>
          </div>
        ))}
      </div>
    </div>
  );
}

export default TechficientAgentPanel;
